import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUp, ArrowUpRight } from 'lucide-react'
import { useDictionary } from '@/lib/i18n'
import BrandMark from '@/components/generals/brand-mark'
import LanguageSwitch from '@/components/generals/language-switch'

import { OFFICIAL_PRODUCT_URL, PRICE } from '@/constants/bike'

const SiteFooter: FC = (): ReactNode => {
    const t = useDictionary()

    return (
        <footer className='border-t border-border bg-black/40'>
            <div className='mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20'>
                <div className='grid gap-12 md:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr]'>
                    <div>
                        <BrandMark />

                        <p className='mt-5 max-w-sm text-sm leading-relaxed text-muted-foreground'>
                            {t.footer.tagline}
                        </p>

                        <p className='mt-6 font-heading text-3xl font-semibold tracking-tight text-foreground'>
                            {PRICE}
                        </p>
                    </div>

                    <nav aria-label={t.footer.explore}>
                        <p className='font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground'>
                            {t.footer.explore}
                        </p>

                        <ul className='mt-5 grid grid-cols-2 gap-x-6 gap-y-3'>
                            {t.nav.links.map((link) => (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        className='text-sm text-foreground/85 transition-colors hover:text-race-bright'
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div>
                        <p className='font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground'>
                            {t.footer.official}
                        </p>

                        <a
                            href={OFFICIAL_PRODUCT_URL}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='group mt-5 inline-flex items-start gap-2 text-sm text-foreground/85 transition-colors hover:text-race-bright'
                        >
                            {t.footer.officialLink}
                            <ArrowUpRight aria-hidden='true' className='mt-0.5 size-4 shrink-0 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                        </a>

                        <div className='mt-8'>
                            <LanguageSwitch />
                        </div>
                    </div>
                </div>

                <div className='mt-14 flex flex-col gap-6 border-t border-border pt-8 text-xs leading-relaxed text-muted-foreground md:flex-row md:items-center md:justify-between'>
                    <p className='max-w-2xl'>
                        {t.footer.legal}
                    </p>

                    <div className='flex items-center gap-6'>
                        <p className='whitespace-nowrap'>
                            © <span suppressHydrationWarning>{new Date().getFullYear()}</span> Tarmac SL9 LTD
                        </p>

                        <a
                            href='#main'
                            className='inline-flex items-center gap-1.5 whitespace-nowrap text-foreground/85 transition-colors hover:text-race-bright'
                        >
                            {t.footer.backToTop}
                            <ArrowUp aria-hidden='true' className='size-3.5' />
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    )
}

export default SiteFooter
